import { FC, MouseEventHandler } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { Box } from '@mui/material';
import { DeloitteButton } from './DeloitteButton';
import { Check, Info as InfoIcon } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        borderRadius: theme.deloitte.radius,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: `4px ${theme.palette.background.paper} solid`,
        minHeight: '360px'
    },
    header: {
        padding: '40px 40px 18px 40px',
        alignItems: 'start',
        minHeight: '122px'
    },
    headerTitle: {
        fontSize: '2.0em',
        fontWeight: 300,
        lineHeight: 1,
    },
    offerHeader: {
        padding: '40px 40px 0px 40px',
        /* height: '200px'*/
    },
    offerHeaderTitle: {
        fontSize: '18px',
        fontWeight: 400,
        color: '#6d6d6d',
        lineHeight: '24px',
        padding: '0px 0 18px 0'
    },
    headerSubtitle: {
        fontSize: '18px',
        fontWeight: 400,
        color: '#6d6d6d',
        lineHeight: '24px',
        padding: '18px 0 0 0'
    },
    centered: {
        textAlign: "center"
    },
    pos: {
        marginBottom: 12,
        fontWeight: 300,
    },
    content: {
        padding: '18px 40px',
        flexGrow: 1
    },
    action: {
        padding: '18px 40px 40px 40px',
        justifyContent: "center"
    },
    mainButton: {
        marginTop: '18px',
        padding: '18px 34px'
    },
    infoButton: {
        color: theme.deloitte.brand
    }
}));

interface DeloitteCardProps {
    title: string;
    text?: string[];
    buttonLabel?: string;
    variant?: "outlined" | "contained" | "text";
    color?: string;
    clear?: boolean;
    offerTitle?: string;
    cta?: boolean;
    selected?: boolean;
    onClickInfo?: MouseEventHandler;
    onClick?: MouseEventHandler;
}

interface DeloitteServiceCardProps {
    title: string;
    subtitle?: string;
    text?: string[];
    buttonLabel?: string;
    variant?: "outlined" | "contained" | "text";
    color?: string;
    clear?: boolean;
    cta?: boolean;
    selected?: boolean;
    onClickInfo?: MouseEventHandler;
    onClick?: MouseEventHandler;
}

export const DeloitteCard: FC<DeloitteCardProps> = (props) => {
    const classes = useStyles();
    const { title, text, buttonLabel, children, variant, color, clear, offerTitle, onClick, cta, selected, onClickInfo } = props;
    const buttonColor = clear ? undefined : color ?? "secondary";
    const cardHeaderAction = onClickInfo ? (
        <IconButton
            aria-label="info"
            onClick={onClickInfo}
            className={classes.infoButton}
            size="large">
            <InfoIcon />
        </IconButton>) : null;
    return (
        <Card className={classes.root} elevation={selected ? 12 : 2}>
            {
                offerTitle &&
                <Box className={classes.offerHeader}>
                    <Typography className={`${classes.offerHeaderTitle} ${classes.centered}`}>{offerTitle}</Typography>
                    <Typography className={`${classes.headerTitle} ${classes.centered}`}>{title}</Typography>
                </Box>
            }
            {!offerTitle &&
                <CardHeader title={title} classes={{ root: classes.header, title: classes.headerTitle }} action={cardHeaderAction} />
            }
            <CardContent className={classes.content}>
                {text && text.map((t, index) =>
                    <Typography className={classes.pos} color="textSecondary" key={index}>
                        {t}
                    </Typography>
                )
                }
                {children}
            </CardContent>
            {buttonLabel &&
                <CardActions className={classes.action}>
                    <DeloitteButton variant={variant ?? "contained"} size="large" color={buttonColor as any} onClick={onClick} cta={cta}>{selected ? <Check /> : buttonLabel}</DeloitteButton>
                </CardActions>
            }
        </Card>
    );
}

export const DeloitteServiceCard: FC<DeloitteServiceCardProps> = (props) => {
    const classes = useStyles();
    const { title, text, buttonLabel, children, variant, color, clear, subtitle, onClick, cta, selected, onClickInfo } = props;
    const buttonColor = clear ? undefined : color ?? "secondary";
    const cardHeaderAction = onClickInfo ? (
        <IconButton
            aria-label="info"
            onClick={onClickInfo}
            className={classes.infoButton}
            size="large">
            <InfoIcon />
        </IconButton>) : null;
    return (
        <Card className={classes.root} elevation={selected ? 12 : 2}>
            {
                subtitle &&
                <Box className={classes.offerHeader}>
                    <Typography className={classes.headerTitle}>{title}</Typography>
                    <Typography className={classes.headerSubtitle}>{subtitle}</Typography>
                </Box>
            }
            {!subtitle &&
                <CardHeader title={title} classes={{ root: classes.header, title: classes.headerTitle }} action={cardHeaderAction} />
            }
            <CardContent className={classes.content}>
                {text && text.map((t, index) =>
                    <Typography className={classes.pos} color="textSecondary" key={index}>
                        {t}
                    </Typography>
                )
                }
                {children}
            </CardContent>
            {buttonLabel &&
                <CardActions className={classes.action}>
                    <DeloitteButton variant={variant ?? "contained"} size="large" color={buttonColor as any} onClick={onClick} cta={cta}>{selected ? <Check /> : buttonLabel}</DeloitteButton>
                </CardActions>
            }
        </Card>
    );
}
